/**
 * scaleConstraints - Mindestausdehnung beim Skalieren von Canvas-Objekten (#699)
 *
 * Hintergrund: Der 2D-Editor ließ eine Dimension über die Scale-Handles
 * beliebig klein werden. `buildSceneModel` verwirft aber jedes Polygon unter
 * `MIN_AREA_M2` (2 cm × 2 cm) als entartet — ein auf ~0 gezogenes Objekt ist
 * in 2D noch ein anklickbarer Strich, in 3D aber ersatzlos weg.
 *
 * Fix an der Quelle: eine Mindestausdehnung erzwingen, damit der Widerspruch
 * gar nicht erst entsteht. Editor-Konvention ist fix 1 px = 1 cm (siehe
 * scene3d/units.js — buildSceneModel wird ohne pixelsPerCm aufgerufen), die
 * Untergrenze ist damit 2 px, dasselbe Kriterium wie MIN_AREA_M2.
 *
 * Die Funktionen sind bewusst rein (keine Fabric-Abhängigkeit), damit der
 * gemeinsame Skalier-Pfad in GardenCanvas.jsx sie für alle skalierbaren Typen
 * nutzen kann und sie ohne Canvas-Mock testbar bleiben.
 */

/** Untergrenze pro Objekt-Dimension in Canvas-Pixeln (= 2 cm bei 1 px = 1 cm). */
export const MIN_OBJECT_DIMENSION_PX = 2;

/**
 * Snap + Mindestausdehnung für ein über Scale-Handles skaliertes Objekt.
 *
 * Rechnet die von Fabric bereits angewandte Skalierung auf gesnappte und
 * geklemmte Zielmaße um und führt die Position so nach, dass die gezogene
 * Ecke wandert und die gegenüberliegende fix bleibt (aus dem Ursprungs-
 * Zustand `original`, wie der Bestandscode). Zentrums-Ursprung (Ellipsen)
 * hält den Mittelpunkt.
 *
 * @param {object} params
 * @param {number} params.baseW - unskalierte lokale Breite in px (Ellipse: rx*2)
 * @param {number} params.baseH - unskalierte lokale Höhe in px (Ellipse: ry*2)
 * @param {number} params.scaleX - aktuell von Fabric angewandtes scaleX
 * @param {number} params.scaleY - aktuell von Fabric angewandtes scaleY
 * @param {string} [params.corner] - aktiver Handle ('tl','br','ml','mt',...)
 * @param {boolean} [params.hasCenterOrigin] - Objekt mit Zentrums-Ursprung
 * @param {{enabled: boolean, gridSize: number}} [params.snap] - Snap-Zustand
 * @param {{left:number, top:number, scaleX:number, scaleY:number}} params.original
 *        - Zustand bei Transform-Beginn (e.transform.original)
 * @param {number} [params.minDimension=MIN_OBJECT_DIMENSION_PX]
 * @returns {{scaleX:number, scaleY:number, left:number, top:number} | null}
 *          null ⇒ keine Korrektur nötig (Fabric-Transform unangetastet lassen)
 */
export function resolveScalingTransform({
  baseW,
  baseH,
  scaleX,
  scaleY,
  corner,
  hasCenterOrigin = false,
  snap,
  original,
  minDimension = MIN_OBJECT_DIMENSION_PX,
}) {
  // Ohne echte Grundmaße keine sinnvolle Umrechnung (Division durch 0) —
  // Fabric-Transform unangetastet lassen.
  if (!(baseW > 0) || !(baseH > 0)) {
    return null;
  }

  const snapping = Boolean(snap?.enabled) && snap.gridSize > 0;

  const effectiveW = baseW * scaleX;
  const effectiveH = baseH * scaleY;

  let targetW = effectiveW;
  let targetH = effectiveH;
  if (snapping) {
    targetW = Math.round(effectiveW / snap.gridSize) * snap.gridSize;
    targetH = Math.round(effectiveH / snap.gridSize) * snap.gridSize;
  }

  // Mindestausdehnung erzwingen — verhindert das Verschwinden in 3D (#699).
  targetW = Math.max(targetW, minDimension);
  targetH = Math.max(targetH, minDimension);

  // Ohne Snapping und wenn beide Maße die Untergrenze schon einhalten:
  // Fabrics Transform nicht überschreiben. Das lässt den Normalfall exakt
  // (auch rotationsgenau) und greift nur an der entarteten Grenze ein.
  if (!snapping && targetW === effectiveW && targetH === effectiveH) {
    return null;
  }

  const newScaleX = targetW / baseW;
  const newScaleY = targetH / baseH;

  let left = original.left;
  let top = original.top;

  if (!hasCenterOrigin) {
    // Top-Left-Ursprung: Position anhand der gezogenen Kante nachführen, damit
    // die gegenüberliegende Ecke fix bleibt.
    let leftAdjust = 0;
    let topAdjust = 0;
    if (corner && corner.includes('l')) {
      leftAdjust = (original.scaleX - newScaleX) * baseW;
    }
    if (corner && corner.includes('t')) {
      topAdjust = (original.scaleY - newScaleY) * baseH;
    }
    left = original.left + leftAdjust;
    top = original.top + topAdjust;
  }

  return { scaleX: newScaleX, scaleY: newScaleY, left, top };
}

/**
 * Mindestlänge für den Linien-Sonderpfad (Linien tragen ihre Geometrie in
 * x1..y2, nicht in scaleX/scaleY). Hält den gezogenen Endpunkt mindestens
 * `minLength` px vom Fixpunkt entfernt, ohne die Richtung zu ändern.
 *
 * @param {object} params
 * @param {number} params.x1
 * @param {number} params.y1
 * @param {number} params.x2
 * @param {number} params.y2
 * @param {boolean} params.draggingP1 - true ⇒ (x1,y1) wird gezogen, (x2,y2) fix
 * @param {number} [params.minLength=MIN_OBJECT_DIMENSION_PX]
 * @returns {{x:number, y:number} | null} neue Koordinaten des gezogenen
 *          Endpunkts, oder null wenn die Länge bereits ausreicht
 */
export function clampLineEndpoint({
  x1,
  y1,
  x2,
  y2,
  draggingP1,
  minLength = MIN_OBJECT_DIMENSION_PX,
}) {
  const fixedX = draggingP1 ? x2 : x1;
  const fixedY = draggingP1 ? y2 : y1;
  const dragX = draggingP1 ? x1 : x2;
  const dragY = draggingP1 ? y1 : y2;

  const dx = dragX - fixedX;
  const dy = dragY - fixedY;
  const length = Math.hypot(dx, dy);

  if (length >= minLength) {
    return null;
  }

  // Bei Länge 0 fehlt die Richtung — entlang der X-Achse aufschieben.
  const ux = length === 0 ? 1 : dx / length;
  const uy = length === 0 ? 0 : dy / length;

  return {
    x: fixedX + ux * minLength,
    y: fixedY + uy * minLength,
  };
}
